import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/contexts/useAuth';
import { createAdminApiClient, AdminApiError } from '@/lib/admin-api-client';
import { cognitoPasswordSchema } from '@/lib/admin-validation';
import type { AdminUser } from '@/lib/admin-types';
import type { UserRole } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

/** Format an E.164 phone number for display: +12145555555 → +1 (214) 555-5555 */
function formatPhone(phone: string): string {
  if (!phone) return '';
  // Match +1 followed by 10 digits (US/Canada)
  const match = phone.match(/^\+1(\d{3})(\d{3})(\d{4})$/);
  if (match) return `+1 (${match[1]}) ${match[2]}-${match[3]}`;
  // For non-US numbers, just return as-is
  return phone;
}

export default function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const { idToken, user: currentUser } = useAuth();
  const navigate = useNavigate();

  const [user, setUser] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Role change state
  const [selectedRole, setSelectedRole] = useState<UserRole>('user');
  const [roleUpdating, setRoleUpdating] = useState(false);
  const [roleMessage, setRoleMessage] = useState<string | null>(null);

  // Identity edit state
  const [editingIdentity, setEditingIdentity] = useState(false);
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [identityUpdating, setIdentityUpdating] = useState(false);
  const [identityMessage, setIdentityMessage] = useState<string | null>(null);

  // Enable/disable state
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);

  // Reset password state
  const [showResetForm, setShowResetForm] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [resetUpdating, setResetUpdating] = useState(false);
  const [resetMessage, setResetMessage] = useState<string | null>(null);

  // Delete user state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteUpdating, setDeleteUpdating] = useState(false);

  const isSelf = currentUser?.userId === userId;

  const client = useMemo(
    () => createAdminApiClient(
      import.meta.env.VITE_API_GATEWAY_BASE_URL,
      () => idToken ?? '',
    ),
    [idToken],
  );

  const fetchUser = useCallback(async () => {
    if (!userId) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await client.getUser(userId);
      setUser(data);
      setSelectedRole(data.role);
      setEditEmail(data.email);
      setEditPhone(data.phoneNumber ?? '');
    } catch (err) {
      if (err instanceof AdminApiError) {
        if (err.status === 401) {
          navigate('/sign-in', { state: { message: 'Session expired' } });
          return;
        }
        if (err.status === 403) {
          setError('You do not have permission to view this user.');
          return;
        }
        if (err.status === 404) {
          setError('User not found.');
          return;
        }
        setError(err.errorMessage);
        console.error(`[UserDetailPage] GET /users/${userId}`, err.status, err.errorMessage);
      } else {
        setError('Unable to connect to the server');
        console.error(`[UserDetailPage] GET /users/${userId}`, err);
      }
    } finally {
      setIsLoading(false);
    }
  }, [userId, client, navigate]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const handleRoleChange = async () => {
    if (!userId || !user || selectedRole === user.role) return;
    setRoleUpdating(true);
    setRoleMessage(null);
    try {
      await client.updateUserRole(userId, selectedRole);
      setUser((prev) => (prev ? { ...prev, role: selectedRole } : prev));
      setRoleMessage('Role updated successfully.');
    } catch (err) {
      if (err instanceof AdminApiError) {
        setRoleMessage(err.errorMessage);
        setSelectedRole(user.role);
        console.error(`[UserDetailPage] PUT /users/${userId}/role`, err.status, err.errorMessage);
      } else {
        setRoleMessage('Failed to update role');
        setSelectedRole(user.role);
        console.error(`[UserDetailPage] PUT /users/${userId}/role`, err);
      }
    } finally {
      setRoleUpdating(false);
    }
  };

  const handleUpdateIdentity = async () => {
    if (!userId || !user) return;
    const attrs: { email?: string; phoneNumber?: string } = {};
    if (editEmail !== user.email) attrs.email = editEmail;
    if (editPhone !== (user.phoneNumber ?? '')) attrs.phoneNumber = editPhone;
    if (Object.keys(attrs).length === 0) {
      setEditingIdentity(false);
      return;
    }
    setIdentityUpdating(true);
    setIdentityMessage(null);
    try {
      await client.updateUserAttributes(userId, attrs);
      setUser((prev) =>
        prev
          ? { ...prev, email: editEmail, phoneNumber: editPhone || undefined }
          : prev,
      );
      setIdentityMessage('Identity updated.');
      setEditingIdentity(false);
    } catch (err) {
      if (err instanceof AdminApiError) {
        setIdentityMessage(err.errorMessage);
        console.error(`[UserDetailPage] PUT /users/${userId}/attributes`, err.status, err.errorMessage);
      } else {
        setIdentityMessage('Failed to update identity');
        console.error(`[UserDetailPage] PUT /users/${userId}/attributes`, err);
      }
    } finally {
      setIdentityUpdating(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!userId || !user) return;
    setStatusUpdating(true);
    setStatusMessage(null);
    setShowDisableConfirm(false);
    try {
      if (user.enabled) {
        await client.disableUser(userId);
        setUser((prev) => (prev ? { ...prev, enabled: false } : prev));
        setStatusMessage('User disabled.');
      } else {
        await client.enableUser(userId);
        setUser((prev) => (prev ? { ...prev, enabled: true } : prev));
        setStatusMessage('User enabled.');
      }
    } catch (err) {
      if (err instanceof AdminApiError) {
        setStatusMessage(err.errorMessage);
        console.error(`[UserDetailPage] PUT /users/${userId}/${user.enabled ? 'disable' : 'enable'}`, err.status, err.errorMessage);
      } else {
        setStatusMessage('Failed to update status');
        console.error(`[UserDetailPage] PUT /users/${userId}/${user.enabled ? 'disable' : 'enable'}`, err);
      }
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleResetPassword = async () => {
    setPasswordError(null);
    setResetMessage(null);
    const result = cognitoPasswordSchema.safeParse(newPassword);
    if (!result.success) {
      setPasswordError(result.error.issues[0].message);
      return;
    }
    if (!userId) return;
    setResetUpdating(true);
    try {
      await client.resetUserPassword(userId, newPassword);
      setResetMessage('Password reset. User must sign in with the temporary password.');
      setNewPassword('');
      setShowResetForm(false);
    } catch (err) {
      if (err instanceof AdminApiError) {
        setResetMessage(err.errorMessage);
        console.error(`[UserDetailPage] POST /users/${userId}/reset-password`, err.status, err.errorMessage);
      } else {
        setResetMessage('Failed to reset password');
        console.error(`[UserDetailPage] POST /users/${userId}/reset-password`, err);
      }
    } finally {
      setResetUpdating(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!userId) return;
    setDeleteUpdating(true);
    try {
      await client.deleteUser(userId);
      navigate('/admin');
    } catch (err) {
      if (err instanceof AdminApiError) {
        console.error(`[UserDetailPage] DELETE /users/${userId}`, err.status, err.errorMessage);
      } else {
        console.error(`[UserDetailPage] DELETE /users/${userId}`, err);
      }
      setDeleteUpdating(false);
      setShowDeleteConfirm(false);
    }
  };

  if (isLoading) {
    return (
      <section aria-label="User Detail" className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </section>
    );
  }

  if (error) {
    return (
      <section aria-label="User Detail" className="space-y-4">
        <div className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/5 p-6 text-destructive" role="alert">
          <AlertTriangle className="size-5 shrink-0" aria-hidden="true" />
          <p className="flex-1">{error}</p>
          {error === 'User not found.' ? (
            <Button variant="outline" size="sm" onClick={() => navigate('/admin')}>
              Back to Users
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={fetchUser}>
              Retry
            </Button>
          )}
        </div>
      </section>
    );
  }

  if (!user) return null;

  return (
    <section aria-label="User Detail" className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin')} aria-label="Back to users">
          <ArrowLeft className="size-4" />
        </Button>
        <h2 className="text-2xl font-semibold">{user.email}</h2>
      </div>

      {/* Identity Section */}
      <Card>
        <CardHeader>
          <CardTitle>Identity</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {!editingIdentity ? (
            <>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Email</span>
                  <span>{user.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Phone Number</span>
                  <span>{formatPhone(user.phoneNumber ?? '') || 'Not provided'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Email Verified</span>
                  <span>{user.emailVerified ? 'Yes' : 'No'}</span>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => setEditingIdentity(true)}>
                Edit
              </Button>
              {identityMessage && (
                <p className="text-sm text-muted-foreground">{identityMessage}</p>
              )}
            </>
          ) : (
            <div className="max-w-sm space-y-3">
              <div>
                <label htmlFor="editEmail" className="mb-1 block text-sm font-medium">Email</label>
                <Input
                  id="editEmail"
                  type="email"
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                  disabled={identityUpdating}
                />
              </div>
              <div>
                <label htmlFor="editPhone" className="mb-1 block text-sm font-medium">
                  Phone Number <span className="text-muted-foreground">(optional)</span>
                </label>
                <Input
                  id="editPhone"
                  type="tel"
                  placeholder="+12125551234"
                  value={editPhone}
                  onChange={(e) => setEditPhone(e.target.value)}
                  disabled={identityUpdating}
                />
                <p className="mt-1 text-xs text-muted-foreground">E.164 format: +country code and number, no spaces (e.g. +12125551234)</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleUpdateIdentity} disabled={identityUpdating}>
                  {identityUpdating ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setEditingIdentity(false);
                    setEditEmail(user.email);
                    setEditPhone(user.phoneNumber ?? '');
                  }}
                >
                  Cancel
                </Button>
              </div>
              {identityMessage && (
                <p className="text-sm text-destructive">{identityMessage}</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Access Control Section */}
      <Card>
        <CardHeader>
          <CardTitle>Access Control</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium">Role</p>
              {isSelf && (
                <p className="text-xs text-muted-foreground">Cannot change your own role</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Select
                value={selectedRole}
                onValueChange={(val) => setSelectedRole(val as UserRole)}
                disabled={isSelf || roleUpdating}
              >
                <SelectTrigger aria-label="User role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="read-only">Read-only</SelectItem>
                </SelectContent>
              </Select>
              <Button
                size="sm"
                disabled={isSelf || roleUpdating || selectedRole === user.role}
                onClick={handleRoleChange}
              >
                {roleUpdating ? 'Saving...' : 'Update'}
              </Button>
            </div>
          </div>
          {roleMessage && (
            <p className="text-sm text-muted-foreground">{roleMessage}</p>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Account State</span>
            <span>{user.userStatus}</span>
          </div>
        </CardContent>
      </Card>

      {/* Account Status Section */}
      <Card>
        <CardHeader>
          <CardTitle>Account Status</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">
                {user.enabled ? 'Enabled' : 'Disabled'}
              </p>
              {isSelf && (
                <p className="text-xs text-muted-foreground">Cannot disable your own account</p>
              )}
            </div>
            {!showDisableConfirm ? (
              <Button
                variant={user.enabled ? 'destructive' : 'default'}
                size="sm"
                disabled={isSelf || statusUpdating}
                onClick={() => {
                  if (user.enabled) {
                    setShowDisableConfirm(true);
                  } else {
                    handleToggleStatus();
                  }
                }}
              >
                {user.enabled ? 'Disable' : 'Enable'}
              </Button>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-sm text-destructive">Confirm disable?</span>
                <Button variant="destructive" size="sm" onClick={handleToggleStatus} disabled={statusUpdating}>
                  {statusUpdating ? 'Disabling...' : 'Yes'}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShowDisableConfirm(false)}>
                  No
                </Button>
              </div>
            )}
          </div>
          {statusMessage && (
            <p className="text-sm text-muted-foreground">{statusMessage}</p>
          )}
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Created</span>
              <span>{new Date(user.createdAt).toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Last Modified</span>
              <span>{new Date(user.lastModifiedAt).toLocaleString()}</span>
            </div>
            {user.lastSignInAt && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Last Sign-In</span>
                <span>{new Date(user.lastSignInAt).toLocaleString()}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Reset Password Section */}
      <Card>
        <CardHeader>
          <CardTitle>Reset Password</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {!showResetForm ? (
            <Button variant="outline" onClick={() => setShowResetForm(true)}>
              Reset Password
            </Button>
          ) : (
            <div className="max-w-sm space-y-3">
              <div>
                <label htmlFor="newPassword" className="mb-1 block text-sm font-medium">
                  New Temporary Password
                </label>
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  aria-invalid={!!passwordError}
                  disabled={resetUpdating}
                />
                {passwordError && (
                  <p className="mt-1 text-sm text-destructive">{passwordError}</p>
                )}
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleResetPassword} disabled={resetUpdating}>
                  {resetUpdating ? 'Resetting...' : 'Confirm Reset'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowResetForm(false);
                    setNewPassword('');
                    setPasswordError(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
          {resetMessage && (
            <p className="text-sm text-muted-foreground">{resetMessage}</p>
          )}
        </CardContent>
      </Card>

      {/* Delete User Section */}
      {!isSelf && (
        <Card>
          <CardHeader>
            <CardTitle className="text-destructive">Danger Zone</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {!showDeleteConfirm ? (
              <div>
                <Button variant="destructive" onClick={() => setShowDeleteConfirm(true)}>
                  Delete User
                </Button>
                <p className="mt-2 text-xs text-muted-foreground">
                  Permanently remove this user from the system.
                </p>
              </div>
            ) : (
              <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-4 space-y-3">
                <p className="text-sm font-medium text-destructive">Are you sure you want to delete this user?</p>
                <ul className="text-xs text-muted-foreground space-y-1 list-disc pl-4">
                  <li>This action is permanent and cannot be undone</li>
                  <li>The user will immediately lose access to all systems</li>
                  <li>All user data in Cognito will be permanently removed</li>
                  <li>If the user needs access again, a new account must be created</li>
                </ul>
                <div className="flex gap-2 pt-1">
                  <Button variant="destructive" size="sm" onClick={handleDeleteUser} disabled={deleteUpdating}>
                    {deleteUpdating ? 'Deleting...' : 'Yes, Delete User'}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setShowDeleteConfirm(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </section>
  );
}
